import { Presenter } from '../../../common/ddd/presenter.base';
import { LogManager } from '../../../common/port/log/log.manager.port';
import { SelfCalendarScoreAggregateRoot } from '../../../core/domain/aggregates/scores/self-calendar-score.aggregate-root';
import { SelfDigitalIntensityScoreAggregateRoot } from '../../../core/domain/aggregates/scores/self-digital-intensity-score.aggregate-root';
import { SelfDigitalWorkingHoursScoreAggregateRoot } from '../../../core/domain/aggregates/scores/self-digital-working-hours-score.aggregate-root';
import { SelfFocusScoreAggregateRoot } from '../../../core/domain/aggregates/scores/self-focus-score.aggregate-root';
import { SelfScreentimeScoreAggregateRoot } from '../../../core/domain/aggregates/scores/self-screentime-score.aggregate-root';
import { SelfTimeInCallsScoreAggregateRoot } from '../../../core/domain/aggregates/scores/self-time-in-calls-score.aggregate-root';
import { SelfGetAllScoresResponseDto } from '../../dtos/scores/self-get-all-scores.response.dto';
import { SelfCalendarScoreAggregateRootMapper } from '../../../infrastructure/mappers/scores/self-calendar-score-aggregate-root.mapper';
import { SelfDigitalIntensityScoreAggregateRootMapper } from '../../../infrastructure/mappers/scores/self-digital-intensity-score-aggregate-root.mapper';
import { SelfDigitalWorkingHoursScoreAggregateRootMapper } from '../../../infrastructure/mappers/scores/self-digital-working-hours-score-aggregate-root.mapper';
import { SelfFocusScoreAggregateRootMapper } from '../../../infrastructure/mappers/scores/self-focus-score-aggregate-root.mapper';
import { SelfScreentimeScoreAggregateRootMapper } from '../../../infrastructure/mappers/scores/self-screentime-score-aggregate-root.mapper';
import { SelfTimeInCallsScoreAggregateRootMapper } from '../../../infrastructure/mappers/scores/self-time-in-calls-score-aggregate-root.mapper';
export interface GetSelfAllScoresPresenterProps {
  digitalIntensityScore: SelfDigitalIntensityScoreAggregateRoot;
  digitalWorkingHoursScore: SelfDigitalWorkingHoursScoreAggregateRoot;
  focusScore: SelfFocusScoreAggregateRoot;
  screentimeScore: SelfScreentimeScoreAggregateRoot;
  calendarScore: SelfCalendarScoreAggregateRoot;
  timeInCallsScore: SelfTimeInCallsScoreAggregateRoot;
}

export class GetSelfAllScoresPresenter
  implements
    Presenter<
      GetSelfAllScoresPresenterProps,
      Omit<SelfGetAllScoresResponseDto, 'startDate' | 'endDate'>
    >
{
  constructor(
    protected readonly logManager: LogManager,
    protected readonly digitalIntensityMapper: SelfDigitalIntensityScoreAggregateRootMapper,
    protected readonly digitalWorkingHoursMapper: SelfDigitalWorkingHoursScoreAggregateRootMapper,
    protected readonly focusMapper: SelfFocusScoreAggregateRootMapper,
    protected readonly screentimeMapper: SelfScreentimeScoreAggregateRootMapper,
    protected readonly calendarMapper: SelfCalendarScoreAggregateRootMapper,
    protected readonly timeInCallsMapper: SelfTimeInCallsScoreAggregateRootMapper
  ) {}

  /**
   * Remove legacy 'hasData' property from the response once the frontend is updated to handle empty data
   */
  toResponse(
    entities: GetSelfAllScoresPresenterProps
  ): Omit<SelfGetAllScoresResponseDto, 'startDate' | 'endDate'> {
    try {
      const digitalIntensity = this.digitalIntensityMapper.toResponse(
        entities.digitalIntensityScore
      );
      const digitalWorkingHours = this.digitalWorkingHoursMapper.toResponse(
        entities.digitalWorkingHoursScore
      );
      const focus = this.focusMapper.toResponse(entities.focusScore);
      const screentime = this.screentimeMapper.toResponse(
        entities.screentimeScore
      );
      const calendar = this.calendarMapper.toResponse(entities.calendarScore);
      const timeInCalls = this.timeInCallsMapper.toResponse(
        entities.timeInCallsScore
      );

      const hasData =
        !digitalIntensity ||
        !digitalWorkingHours ||
        !focus ||
        !screentime ||
        !calendar ||
        !timeInCalls
          ? false
          : true;

      return {
        hasData,
        digitalIntensity,
        workingHours: digitalWorkingHours,
        focus,
        screenTime: screentime,
        timeInMeetings: calendar,
        timeInCalls,
      };
    } catch (error) {
      throw new Error(
        `GetSelfAllScoresPresenter.toResponse encountered an error ${error}`
      );
    }
  }
}
